/**
 * Où a lieu un événement, quand le jeu de données ne le dit pas franchement.
 *
 * Beaucoup de fiches portent les coordonnées 48.856578, 2.351828. Ce n'est
 * pas un lieu, c'est le centre de Paris posé par défaut quand la source n'a
 * rien su géocoder : 59 fiches sur 600 s'y retrouvent empilées. Les afficher
 * telles quelles plantait une épingle sur l'Hôtel de Ville et proposait un
 * itinéraire pour y aller, ce qui est pire que de ne rien dire. Le Théâtre
 * le Funambule, à Montmartre, était annoncé place de l'Hôtel de Ville.
 *
 * Deux cas derrière ce repère :
 *
 *  - La fiche a quand même une adresse en toutes lettres. On la géocode
 *    auprès de la Base Adresse Nationale, et on retrouve le vrai point. Sur
 *    douze essais, onze tombent juste.
 *
 *  - La fiche n'a aucune adresse. C'est en général un programme couvrant
 *    plusieurs endroits : les piscines parisiennes, les animations en
 *    bibliothèque, des parcours guidés. Il n'y a pas d'adresse parce qu'il
 *    n'y en a pas. On le dit, avec les mots que le jeu de données emploie
 *    lui-même ailleurs : « Plusieurs lieux dans Paris ».
 */

// Le point que la source pose quand elle ne sait pas. Valeur exacte, donc
// comparaison exacte : aucun lieu réel ne tombe dessus par hasard.
const REPERE_INCONNU = { lat: 48.856578, lon: 2.351828 };

// Ce que la source écrit déjà quand un événement couvre plusieurs endroits.
export const PLUSIEURS_LIEUX = "Plusieurs lieux dans Paris";

const BAN = "https://api-adresse.data.gouv.fr/search/";

/** Les coordonnées de la fiche désignent-elles vraiment quelque chose ? */
export function coordonneesFiables(item) {
  const c = item?.lat_lon;
  if (!c || typeof c.lat !== "number" || typeof c.lon !== "number") return false;
  return !(c.lat === REPERE_INCONNU.lat && c.lon === REPERE_INCONNU.lon);
}

/**
 * L'adresse à géocoder, ou null.
 *
 * Le nom de rue est exigé : sans lui, la Base Adresse cherche un nom de
 * salle qu'elle ne connaît pas et répond à côté. « Théâtre le Funambule
 * Montmartre » seul tombait à 1,2 km du vrai ; « 53 rue des Saules » tombe
 * sur le numéro exact. Et toutes les fiches concernées ont une rue, donc
 * l'exigence ne coûte rien.
 */
export function adresseTexte(item) {
  const rue = item?.address_street && String(item.address_street).trim();
  if (!rue) return null;

  const commune = [item?.address_zipcode, item?.address_city].filter(Boolean).join(" ");
  const texte = [rue, commune].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();

  // Inutile de géocoder une adresse qui dit elle-même qu'il n'y en a pas.
  if (texte.toLowerCase().includes("plusieurs lieux")) return null;
  return texte;
}

/**
 * Retrouve un point à partir de l'adresse écrite. Retourne null plutôt que
 * d'inventer : mieux vaut pas de carte qu'une épingle au mauvais endroit.
 */
export async function geocoder(adresse, signal) {
  if (!adresse) return null;
  const url = `${BAN}?${new URLSearchParams({ q: adresse, limit: "1" })}`;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    const data = await res.json();
    const trouve = data?.features?.[0];
    if (!trouve) return null;

    // On n'accepte qu'un résultat précis. « municipality » veut dire que la
    // Base Adresse n'a reconnu que la commune, donc qu'elle renverrait le
    // centre de Paris : exactement ce qu'on cherche à éviter.
    const type = trouve.properties?.type;
    if (type !== "housenumber" && type !== "street") return null;

    const [lon, lat] = trouve.geometry?.coordinates || [];
    if (typeof lat !== "number" || typeof lon !== "number") return null;

    // Si la Base Adresse renvoie elle aussi le centre de Paris, c'est qu'elle
    // n'a rien trouvé de mieux : on ne gagne rien à la croire.
    if (
      Math.abs(lat - REPERE_INCONNU.lat) < 0.0005 &&
      Math.abs(lon - REPERE_INCONNU.lon) < 0.0005
    ) {
      return null;
    }
    return { lat, lon };
  } catch {
    // Réseau coupé ou requête annulée : on se passe de carte.
    return null;
  }
}
